import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ClubCard } from '@/components/ClubCard';
import { InterestCheck } from '@/components/InterestCheck';
import { LocalityFilterRow } from '@/components/LocalityFilterRow';
import { PlaceCard } from '@/components/PlaceCard';
import { YogaSessionCard } from '@/components/YogaSessionCard';
import { useFavorites } from '@/hooks/useFavorites';
import { useInterests } from '@/hooks/useInterests';
import { usePlaces } from '@/hooks/usePlaces';
import { clubsForInterestIds } from '@/lib/communityClubs';
import {
  HOUSEHOLD_PROFILES,
  INTEREST_CATALOG,
  interestById,
  pinnedPlaces,
  placesForInterest,
} from '@/lib/interests';
import { sessionsForInterestIds } from '@/lib/yogaSessions';
import {
  groupByLocality,
  parseLocalityFilter,
  placeMatchesLocality,
  type LocalityFilter,
} from '@shared/locality';

/**
 * Yoga sessions and community clubs are Ranch-resident activities
 * (Cypress Lodge, Palmetto Park, Lake Timber Lodge, WellWay, BN WGA,
 * Take a Break Book Club). They are not outside-the-Ranch destinations.
 */
function ranchResidentMatchesLocality(filter: LocalityFilter): boolean {
  return filter !== 'outside';
}

export function InterestsPage() {
  const { places, loading } = usePlaces();
  const { isFavorite, toggle } = useFavorites();
  const {
    displayName,
    activeProfile,
    setActiveProfile,
    interestIds,
    placeSlugs,
    hasInterest,
    toggleInterest,
  } = useInterests();
  const { search } = useLocation();
  const navigate = useNavigate();
  const urlLocality = useMemo(() => parseLocalityFilter(new URLSearchParams(search).get('locality')), [search]);
  const [locality, setLocality] = useState<LocalityFilter>(urlLocality);

  useEffect(() => {
    setLocality(urlLocality);
  }, [urlLocality]);

  useEffect(() => {
    const q = new URLSearchParams();
    if (locality !== 'both') q.set('locality', locality);
    const nextSearch = q.toString();
    if (nextSearch !== search.replace(/^\?/, '')) {
      navigate({ pathname: '/favorites', search: nextSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locality]);

  const checkedClubs = useMemo(() => clubsForInterestIds(interestIds), [interestIds]);
  const checkedSessions = useMemo(() => sessionsForInterestIds(interestIds), [interestIds]);
  const checkedCategories = useMemo(
    () => interestIds.map((id) => interestById(id)).filter((i) => i && i.kind === 'category'),
    [interestIds],
  );
  const pinned = useMemo(() => pinnedPlaces(places ?? [], placeSlugs), [places, placeSlugs]);
  const pinnedOnly = useMemo(() => {
    const already = new Set(
      checkedCategories.flatMap((interest) =>
        interest ? placesForInterest(places ?? [], interest).map((p) => p.slug) : [],
      ),
    );
    return pinned.filter((p) => !already.has(p.slug));
  }, [checkedCategories, pinned, places]);

  const visibleSessions = ranchResidentMatchesLocality(locality) ? checkedSessions : [];
  const visibleClubs = ranchResidentMatchesLocality(locality) ? checkedClubs : [];

  const categorySections = useMemo(
    () =>
      checkedCategories.flatMap((interest) => {
        if (!interest) return [];
        const rows = placesForInterest(places ?? [], interest)
          .filter((p) => placeMatchesLocality(p, locality))
          .slice(0, 12);
        return [{ interest, rows }];
      }),
    [checkedCategories, locality, places],
  );

  const visiblePinned = useMemo(
    () => pinnedOnly.filter((p) => placeMatchesLocality(p, locality)),
    [locality, pinnedOnly],
  );

  const localityCounts = useMemo(() => {
    const listed = [
      ...checkedCategories.flatMap((interest) =>
        interest ? placesForInterest(places ?? [], interest) : [],
      ),
      ...pinnedOnly,
    ];
    const seen = new Set<string>();
    const unique = listed.filter((p) => {
      if (seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    });
    const grouped = groupByLocality(unique);
    const ranchResidents = checkedSessions.length + checkedClubs.length;
    return {
      inside: grouped.inside.length + ranchResidents,
      outside: grouped.outside.length,
    };
  }, [checkedCategories, checkedClubs.length, checkedSessions.length, pinnedOnly, places]);

  const hasAnything = interestIds.length > 0 || placeSlugs.length > 0;
  const hasFilteredResults =
    visibleSessions.length > 0 ||
    visibleClubs.length > 0 ||
    categorySections.some((s) => s.rows.length > 0) ||
    visiblePinned.length > 0;

  const placesBrowseSearch = (category: string) => {
    const q = new URLSearchParams();
    q.set('category', category);
    if (locality !== 'both') q.set('locality', locality);
    return `/places?${q.toString()}`;
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{displayName}’s Favorites</h1>
        <p className="page-subtitle">Checked items only — stored on this phone, not an account</p>
      </div>

      <div className="card">
        <p className="note-label" id="household-profile-label">
          Who is this phone for
        </p>
        <div
          className="profile-picker"
          role="group"
          aria-labelledby="household-profile-label"
        >
          {HOUSEHOLD_PROFILES.map((profile) => (
            <button
              key={profile.id}
              type="button"
              className={`profile-picker__btn ${activeProfile === profile.id ? 'is-active' : ''}`}
              aria-pressed={activeProfile === profile.id}
              onClick={() => setActiveProfile(profile.id)}
            >
              {profile.label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          Chip and Lisa keep separate checks on this device. Lisa starts with Yin Yoga, Yoga in the
          Park, Tai Chi, and Sound Bath — not Power Yoga. Family Favorites (the heart, under More)
          are a different list.
        </p>
      </div>

      <LocalityFilterRow
        value={locality}
        onChange={setLocality}
        loading={loading}
        counts={localityCounts}
      />

      <div className="section-title">What I care about</div>
      <div className="chip-row" role="group" aria-label="What I care about">
        {INTEREST_CATALOG.map((interest) => (
          <InterestCheck
            key={interest.id}
            chip
            checked={hasInterest(interest.id)}
            label={interest.label}
            onToggle={() => toggleInterest(interest.id)}
          />
        ))}
      </div>

      {!hasAnything && (
        <div className="empty-state">
          <p>
            Nothing checked yet. Pick Fitness, a yoga class, or Book club — or tap the check on a
            place card.
          </p>
        </div>
      )}

      {hasAnything && !loading && !hasFilteredResults && (
        <div className="empty-state">
          <p>
            {locality === 'outside'
              ? 'Nothing on this list is outside the Ranch. Yoga, clubs, and in-Ranch places stay on Everywhere or In Ranch.'
              : 'Nothing on this list is in the Ranch.'}
          </p>
        </div>
      )}

      {visibleSessions.map((session) => (
        <YogaSessionCard key={session.id} session={session} />
      ))}

      {visibleClubs.map((club) => (
        <ClubCard key={club.id} club={club} />
      ))}

      {categorySections.map(({ interest, rows }) => {
        if (rows.length === 0 && !loading) return null;
        return (
          <div key={interest.id} style={{ marginTop: 16 }}>
            <div className="section-title">{interest.label}</div>
            {loading && <div className="skeleton" style={{ height: 80 }} />}
            {!loading &&
              rows.map((p) => (
                <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
              ))}
            {interest.placeCategory && rows.length > 0 && (
              <Link
                to={placesBrowseSearch(interest.placeCategory)}
                className="btn btn-ghost btn-block"
                style={{ marginTop: 8 }}
              >
                All {interest.label}
              </Link>
            )}
          </div>
        );
      })}

      {visiblePinned.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="section-title">Pinned places</div>
          {visiblePinned.map((p) => (
            <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
          ))}
        </div>
      )}
    </div>
  );
}
